import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import Button from "../../components/ui/Button";
import { defaultDateFormatter, formatDateTime } from "../../Helper/functionHelper";
import useAuthUser from "../../hooks/useAuthUser";
import useModal from "../../hooks/useModal";
import EmployeeActionForm, {
  withdrawFormProps,
  reopenFormProps,
  acceptFormProps,
} from "../../components/grievance/EmployeeActionForm";
import {
  getGrievanceDetails,
  getGrievanceActivity,
  addGrievanceComment,
  downloadGrievanceDocument,
  getUsersByRole,
  investigateGrievance,
  resolveGrievance,
  withdrawGrievance,
  reopenGrievance,
  acceptGrievanceResolution,
} from "../../services/grievance";

// pms_roles.id for "HR Executive" — same hardcoded convention as
// GrievanceList.jsx's HR_EXECUTIVE_ROLE_ID (no central roles constants file).
const HR_EXECUTIVE_ROLE_ID = 3;

// Short fields — rendered two per row, side by side.
const gridItem = (label, value) => (
  <div>
    <div style={{ fontSize: "12px", color: "#666" }}>{label}</div>
    <div style={{ fontSize: "13.5px" }}>{value || "-"}</div>
  </div>
);

// Longer fields (description, resolution, remarks) — full width, one per row.
const fullRow = (label, value) => (
  <div style={{ marginBottom: "16px" }}>
    <div style={{ fontSize: "12px", color: "#666" }}>{label}</div>
    <div style={{ fontSize: "13.5px" }}>{value || "-"}</div>
  </div>
);

const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const GrievanceDetails = () => {
  // Comes straight from the URL and is passed through unmodified to every
  // API call below — the backend decrypts it, same as every other route
  // that takes a grievance id.
  const { grievanceId } = useParams();
  const { loggedInUser } = useAuthUser();
  const { showModal, hideModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [grievance, setGrievance] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPagination, setActivityPagination] = useState({
    pageNo: 1,
    totalPages: 1,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Tab is mirrored in the URL (?tab=details|activity) so it survives a
  // refresh/share/back-button, and each tab only fetches its own data.
  const requestedTab = searchParams.get("tab");
  const initialTab = requestedTab === "activity" ? requestedTab : "details";
  const [tab, setTab] = useState(initialTab);
  const [comment, setComment] = useState("");
  const [statusUpdate, setStatusUpdate] = useState("");
  const [internalNote, setInternalNote] = useState(false);
  const [commentFiles, setCommentFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const commentFileInputRef = useRef(null);

  // Update Assignee — only the HR Manager can reassign (same gate as
  // investigateGrievance on the backend).
  const [showReassign, setShowReassign] = useState(false);
  const [assignMode, setAssignMode] = useState("own");
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [reassignReason, setReassignReason] = useState("");
  const [reassignSubmitting, setReassignSubmitting] = useState(false);

  // What this user may do on this grievance comes from the backend
  // (permissions are computed per case — see #permissionsFor in
  // grievance.service.js), so the UI never re-derives a rule.
  const canAddInternalNote = !!grievance?.can_add_internal_note;
  const allowedStatusUpdates = grievance?.allowed_status_updates || [];

  const handleTabChange = (newTab) => {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  };

  const fetchDetails = async () => {
    try {
      const detailsRes = await getGrievanceDetails(grievanceId);
      setGrievance(detailsRes.data?.data || null);
    } catch (error) {
      console.log("Error fetching grievance details:", error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    setActivityLoading(true);
    try {
      const activityRes = await getGrievanceActivity(grievanceId, 1);
      setActivity(activityRes.data?.data?.entries || []);
      setActivityPagination(
        activityRes.data?.data?.pagination || { pageNo: 1, totalPages: 1 }
      );
    } catch (error) {
      toast.error("Failed to load activity");
    } finally {
      setActivityLoading(false);
    }
  };

  const handleLoadEarlier = async () => {
    setLoadingMore(true);
    try {
      const nextPage = activityPagination.pageNo + 1;
      const res = await getGrievanceActivity(grievanceId, nextPage);
      const olderEntries = res.data?.data?.entries || [];
      setActivity((prev) => [...olderEntries, ...prev]);
      setActivityPagination(res.data?.data?.pagination || activityPagination);
    } catch (error) {
      toast.error("Failed to load earlier activity");
    } finally {
      setLoadingMore(false);
    }
  };

  // Details is needed regardless of tab (the header above the tabs shows
  // grievance_ref/status_label), so it's always fetched. Activity is only
  // fetched on demand, right below, when that tab is actually the active one.
  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [grievanceId]);

  useEffect(() => {
    if (tab === "activity") fetchActivity();
    // eslint-disable-next-line
  }, [tab, grievanceId]);

  useEffect(() => {
    if (!showReassign || assignMode !== "other") return;
    setAssigneesLoading(true);
    getUsersByRole(HR_EXECUTIVE_ROLE_ID)
      .then((res) => setAssignees(res.data?.data || []))
      .catch(() => toast.error("Failed to load HR Executive list"))
      .finally(() => setAssigneesLoading(false));
  }, [showReassign, assignMode]);

  const handleUpdateAssignee = async () => {
    if (reassignSubmitting) return;
    const newOwnerId = assignMode === "own" ? loggedInUser?.id : selectedAssignee?.id;
    if (assignMode === "other" && !selectedAssignee) {
      toast.error("Please select an HR Executive.");
      return;
    }
    const isReassignment = !!grievance.assigned_to && grievance.assigned_to !== newOwnerId;
    if (isReassignment && !reassignReason.trim()) {
      toast.error("Reason is required when reassigning.");
      return;
    }
    setReassignSubmitting(true);
    try {
      await investigateGrievance(grievanceId, {
        priority: grievance.priority || "Medium",
        assigned_to: newOwnerId,
        reason: reassignReason.trim() || undefined,
      });
      toast.success("Assignee updated successfully");
      setShowReassign(false);
      setAssignMode("own");
      setSelectedAssignee(null);
      setReassignReason("");
      await fetchDetails();
      await fetchActivity();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to update assignee"
      );
    } finally {
      setReassignSubmitting(false);
    }
  };

  const handleResolveGrievance = () => {
    const ResolveForm = () => {
      const [findings, setFindings] = useState("");
      const [resolutionNote, setResolutionNote] = useState("");
      const [attachment, setAttachment] = useState(null);
      const [errors, setErrors] = useState({});
      const [loading, setLoading] = useState(false);

      const handleSubmit = async () => {
        const newErrors = {};
        if (!findings.trim()) newErrors.findings = "Findings are required.";
        if (!resolutionNote.trim()) {
          newErrors.resolutionNote = "Resolution shared with employee is required.";
        }
        if (Object.keys(newErrors).length) {
          setErrors(newErrors);
          return;
        }

        setLoading(true);
        try {
          const formData = new FormData();
          formData.append("findings", findings.trim());
          formData.append("resolution_note", resolutionNote.trim());
          if (attachment) formData.append("documents", attachment);

          await resolveGrievance(grievanceId, formData);
          toast.success("Grievance resolved successfully");
          hideModal();
          await fetchDetails();
          await fetchActivity();
        } catch (error) {
          toast.error(
            error?.response?.data?.message || "Failed to resolve grievance"
          );
        } finally {
          setLoading(false);
        }
      };

      return (
        <div className="py-2">
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="standard"
            label="Findings"
            className="mb-3"
            placeholder="Summarize the investigation findings..."
            value={findings}
            onChange={(e) => {
              setFindings(e.target.value);
              setErrors((prev) => ({ ...prev, findings: "" }));
            }}
            error={!!errors.findings}
            helperText={errors.findings}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="standard"
            label="Resolution shared with employee"
            className="mb-3 mt-3"
            placeholder="Describe the action and next steps..."
            value={resolutionNote}
            onChange={(e) => {
              setResolutionNote(e.target.value);
              setErrors((prev) => ({ ...prev, resolutionNote: "" }));
            }}
            error={!!errors.resolutionNote}
            helperText={errors.resolutionNote}
          />
          <div className="mt-3">
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>
              Attachment (optional)
            </div>
            <input
              type="file"
              accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={(e) => setAttachment(e.target.files?.[0] || null)}
            />
          </div>
          <div className="d-flex justify-content-end mt-3 gap-2">
            <Button variant="secondary" onClick={() => hideModal()} disabled={loading}>
              Cancel
            </Button>
            <Button variant="success" onClick={handleSubmit} disabled={loading}>
              {loading ? "Sharing..." : "Share Resolution"}
            </Button>
          </div>
        </div>
      );
    };

    showModal("Resolve Grievance", <ResolveForm />);
  };

  // Replaces edit/delete: the employee who raised the grievance can withdraw
  // it while it's still open. The backend decides via grievance.can_withdraw.
  const refreshAfterEmployeeAction = async () => {
    await fetchDetails();
    if (tab === "activity") await fetchActivity();
  };

  const handleWithdraw = () => {
    showModal(
      "Withdraw Grievance",
      <EmployeeActionForm
        {...withdrawFormProps((reason) => withdrawGrievance(grievanceId, reason))}
        onDone={refreshAfterEmployeeAction}
      />
    );
  };

  // The employee reopens a Resolved/Closed grievance. The backend decides via
  // grievance.can_reopen; HR/Admin can't set Reopened from Update Status.
  const handleReopen = () => {
    showModal(
      "Reopen Grievance",
      <EmployeeActionForm
        {...reopenFormProps((reason) => reopenGrievance(grievanceId, reason))}
        onDone={refreshAfterEmployeeAction}
      />
    );
  };

  // The employee accepts HR's resolution, which closes the case.
  const handleAccept = () => {
    showModal(
      "Accept Resolution",
      <EmployeeActionForm
        {...acceptFormProps((comment) =>
          acceptGrievanceResolution(grievanceId, comment)
        )}
        onDone={refreshAfterEmployeeAction}
      />
    );
  };

  const handleDownload = async (fileName) => {
    try {
      const res = await downloadGrievanceDocument(
        `${grievance.doc_path}${fileName}`
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleSendComment = async () => {
    // Guards against a double POST (double-click, or a click landing before
    // the "Sending..."/disabled re-render commits) — without this, two
    // requests fire and the same message shows up twice in the timeline.
    if (submitting) return;
    // One comment box for both kinds of message — the "Internal note"
    // checkbox decides who can see it (HR only vs. everyone on the case).
    const sendingInternal = canAddInternalNote && internalNote;
    const noteText = comment;
    if (!noteText.trim()) {
      toast.error(
        sendingInternal
          ? "Write an internal note before sending."
          : "Add a message before sending."
      );
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("note", noteText.trim());
      if (statusUpdate !== "") {
        formData.append("status", statusUpdate);
      }
      if (sendingInternal) {
        formData.append("visibility", "internal");
      }
      commentFiles.forEach((file) => formData.append("documents", file));

      await addGrievanceComment(grievanceId, formData);
      const statusChanged = statusUpdate !== "";
      setComment("");
      setStatusUpdate("");
      setInternalNote(false);
      setCommentFiles([]);
      // The file input is uncontrolled — clearing state alone leaves the
      // previously chosen filename(s) displayed in the picker, so the DOM
      // value has to be reset explicitly too.
      if (commentFileInputRef.current) {
        commentFileInputRef.current.value = "";
      }
      await fetchActivity();
      // The status_label shown above the tabs comes from grievance details,
      // not activity — refetch it so a status change reflects immediately.
      if (statusChanged) await fetchDetails();
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to send comment"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: "24px" }}>Loading...</div>;

  if (notFound || !grievance) {
    return (
      <div style={{ padding: "24px" }}>
        <p>Grievance not found.</p>
        <Link to="/grievance" className="btn btn-secondary btn-sm">
          <span className="icon">
            <img src="/public/images/back-arrow.svg" alt="back" />
          </span>
          <span className="text">Back</span>
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px", maxWidth: "1100px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "4px",
        }}
      >
        <h2 style={{ marginBottom: 0 }}>{grievance.grievance_ref}</h2>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          {grievance.can_withdraw && (
            <Button variant="danger" onClick={handleWithdraw}>
              Withdraw Grievance
            </Button>
          )}
          {grievance.can_accept && (
            <Button variant="success" onClick={handleAccept}>
              Accept Resolution
            </Button>
          )}
          {grievance.can_reopen && (
            <Button variant="secondary" onClick={handleReopen}>
              Reopen Grievance
            </Button>
          )}
          {grievance.can_resolve && (
            <Button variant="success" onClick={handleResolveGrievance}>
              Resolve Grievance
            </Button>
          )}
          <Link to="/grievance" className="btn btn-secondary btn-sm">
            <span className="icon">
              <img src="/public/images/back-arrow.svg" alt="back" />
            </span>
            <span className="text">Back</span>
          </Link>
        </div>
      </div>
      <div
        style={{
          fontSize: "13px",
          color: "#666",
          marginBottom: "12px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <span>{grievance.status_label}</span>
        {grievance.sla_due_date && (
          <span style={{ fontSize: "12px" }}>
            Due by {formatDateTime(grievance.sla_due_date)}
          </span>
        )}
        {grievance.is_overdue && (
          <span
            style={{
              fontSize: "11.5px",
              fontWeight: 600,
              padding: "2px 9px",
              borderRadius: "20px",
              background: "#fde8e8",
              color: "#c0392b",
            }}
          >
            Overdue
          </span>
        )}
      </div>

      <ul className="nav nav-tabs" role="tablist" style={{ marginBottom: "20px" }}>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link ${tab === "details" ? "active" : ""}`}
            onClick={() => handleTabChange("details")}
          >
            Details
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link ${tab === "activity" ? "active" : ""}`}
            onClick={() => handleTabChange("activity")}
          >
            Activity
          </button>
        </li>
      </ul>

      {tab === "details" && (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              columnGap: "24px",
              rowGap: "16px",
              marginBottom: "20px",
            }}
          >
            {gridItem("Raised By", grievance.raised_by_name)}
            {gridItem("Raised For", grievance.raised_for_name)}
            {gridItem("Department", grievance.department)}
            {gridItem("Category", grievance.category_name)}
            {gridItem("Sub Category", grievance.subcategory_name)}
            {gridItem("Subject", grievance.subject)}
            {gridItem("Incident Date", defaultDateFormatter(grievance.incident_date))}
            {gridItem("Priority", grievance.priority)}
            {gridItem("Confidentiality", grievance.confidentiality_level)}
            {gridItem("Assigned To", grievance.assigned_to_name)}
            {gridItem("Raised On", formatDateTime(grievance.created_at))}
          </div>

          {fullRow("Description", grievance.description)}
          {fullRow("Preferred Resolution", grievance.preferred_resolution)}

          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "12px", color: "#666" }}>
              Supporting Documents
            </div>
            {grievance.documents?.length > 0 ? (
              <div>
                {grievance.documents.map((fileName) => (
                  <span
                    key={fileName}
                    onClick={() => handleDownload(fileName)}
                    style={{
                      display: "inline-block",
                      marginRight: "8px",
                      marginTop: "6px",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      background: "#eef0fa",
                      color: "#4a5aa8",
                      fontSize: "12.5px",
                      cursor: "pointer",
                    }}
                  >
                    📎 {fileName}
                  </span>
                ))}
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        </div>
      )}

      {tab === "activity" && (
        <div>
          {canAddInternalNote && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e3e6ec",
                borderRadius: "10px",
                padding: "14px 16px",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "13px" }}>
                  Assigned to:{" "}
                  <strong>{grievance.assigned_to_name || "Unassigned"}</strong>
                </div>
                {grievance.can_assign && !showReassign && (
                  <Button variant="secondary" onClick={() => setShowReassign(true)}>
                    Update Assignee
                  </Button>
                )}
              </div>

              {grievance.can_assign && showReassign && (
                <div style={{ marginTop: "14px" }}>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                    <Button
                      type="button"
                      variant={assignMode === "own" ? "success" : "secondary"}
                      onClick={() => {
                        setAssignMode("own");
                        setSelectedAssignee(null);
                      }}
                    >
                      Take Ownership
                    </Button>
                    <Button
                      type="button"
                      variant={assignMode === "other" ? "success" : "secondary"}
                      onClick={() => {
                        setAssignMode("other");
                        setSelectedAssignee(null);
                      }}
                    >
                      Assign to Other
                    </Button>
                  </div>

                  {assignMode === "other" && (
                    <Autocomplete
                      options={assignees}
                      getOptionLabel={(option) => option.name || ""}
                      value={selectedAssignee}
                      onChange={(e, newValue) => setSelectedAssignee(newValue)}
                      loading={assigneesLoading}
                      renderInput={(params) => (
                        <TextField {...params} variant="standard" label="HR Executive" />
                      )}
                      sx={{ marginBottom: "12px" }}
                    />
                  )}

                  {!!grievance.assigned_to &&
                    grievance.assigned_to !==
                      (assignMode === "own" ? loggedInUser?.id : selectedAssignee?.id) && (
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        variant="standard"
                        label="Reassignment Reason"
                        value={reassignReason}
                        onChange={(e) => setReassignReason(e.target.value)}
                        sx={{ marginBottom: "12px" }}
                      />
                    )}

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                    <Button
                      variant="secondary"
                      onClick={() => setShowReassign(false)}
                      disabled={reassignSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="success"
                      onClick={handleUpdateAssignee}
                      disabled={reassignSubmitting}
                    >
                      {reassignSubmitting ? "Updating..." : "Update"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {activityLoading ? (
            <p style={{ color: "#666" }}>Loading activity...</p>
          ) : activity.length === 0 ? (
            <p style={{ color: "#666" }}>No activity recorded yet.</p>
          ) : (
            <div style={{ marginBottom: "20px" }}>
              {activityPagination.pageNo < activityPagination.totalPages && (
                <div style={{ textAlign: "center", marginBottom: "16px" }}>
                  <span
                    onClick={loadingMore ? undefined : handleLoadEarlier}
                    style={{
                      fontSize: "12.5px",
                      color: "#4a5aa8",
                      cursor: loadingMore ? "default" : "pointer",
                      fontWeight: 600,
                    }}
                  >
                    {loadingMore ? "Loading..." : "Load earlier activity"}
                  </span>
                </div>
              )}
              {activity.map((entry) => (
                <div
                  key={entry.id}
                  style={{ display: "flex", gap: "12px", marginBottom: "18px" }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      background: "#232b42",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {initials(entry.user_name)}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      background: "#fff",
                      border: "1px solid #e3e6ec",
                      borderRadius: "10px",
                      padding: "10px 14px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "10px",
                        marginBottom: "4px",
                      }}
                    >
                      <span style={{ fontWeight: 600, fontSize: "13px" }}>
                        {entry.user_name || "Unknown"}
                      </span>
                      <span style={{ fontSize: "11.5px", color: "#666" }}>
                        {formatDateTime(entry.created_at)}
                      </span>
                    </div>
                    {(entry.status_label || entry.visibility === "internal") && (
                      <div style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                        {entry.status_label && (
                          <div
                            style={{
                              display: "inline-block",
                              fontSize: "11.5px",
                              fontWeight: 600,
                              padding: "2px 9px",
                              borderRadius: "20px",
                              background: "#fdf1dc",
                              color: "#d98e04",
                            }}
                          >
                            {entry.status_label}
                          </div>
                        )}
                        {entry.visibility === "internal" && (
                          <div
                            style={{
                              display: "inline-block",
                              fontSize: "11.5px",
                              fontWeight: 600,
                              padding: "2px 9px",
                              borderRadius: "20px",
                              background: "#eae6fb",
                              color: "#6a4fc4",
                            }}
                          >
                            Internal
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: "13px" }}>{entry.comment}</div>
                    {(entry.attachments || []).map((fileName) => (
                      <div
                        key={fileName}
                        onClick={() => handleDownload(fileName)}
                        style={{
                          display: "inline-block",
                          marginTop: "6px",
                          marginRight: "6px",
                          fontSize: "11.5px",
                          color: "#4a5aa8",
                          background: "#eaecf8",
                          padding: "3px 9px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        📎 {fileName}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              background: "#fff",
              border: "1px solid #e3e6ec",
              borderRadius: "10px",
              padding: "14px 16px",
            }}
          >
            <div style={{ fontSize: "12.5px", fontWeight: 600, marginBottom: "8px" }}>
              Add a comment
            </div>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              placeholder={
                canAddInternalNote && internalNote
                  ? "Write an internal note (visible to HR only)..."
                  : "Type a message..."
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            {allowedStatusUpdates.length > 0 && (
              <TextField
                select
                size="small"
                label="Update Status"
                value={statusUpdate}
                onChange={(e) => setStatusUpdate(e.target.value)}
                sx={{ marginTop: "10px", minWidth: "220px" }}
              >
                <MenuItem value="">No status change</MenuItem>
                {allowedStatusUpdates.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {canAddInternalNote && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={internalNote}
                    onChange={(e) => setInternalNote(e.target.checked)}
                  />
                }
                label="Internal note (visible to HR only)"
                sx={{ display: "block", marginTop: "6px" }}
              />
            )}
            <div style={{ marginTop: "10px" }}>
              <input
                ref={commentFileInputRef}
                type="file"
                multiple
                accept=".doc,.docx,.pdf,.xls,.xlsx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  // Backend accepts multiple files per comment (see
                  // addGrievanceComment) — validate each picked file and
                  // keep every one that passes, so all their names show up
                  // below, not just the last file selected.
                  const pickedFiles = Array.from(e.target.files || []);
                  e.target.value = "";
                  if (!pickedFiles.length) return;

                  const allowedTypes = [
                    ".doc",
                    ".docx",
                    ".pdf",
                    ".xls",
                    ".xlsx",
                    ".jpg",
                    ".jpeg",
                    ".png",
                  ];

                  const validFiles = [];
                  for (const file of pickedFiles) {
                    const ext = "." + file.name.split(".").pop().toLowerCase();
                    if (!allowedTypes.includes(ext)) {
                      toast.error(`"${file.name}" is not a supported file type`);
                      continue;
                    }
                    if (file.size > 4 * 1024 * 1024) {
                      toast.error(`"${file.name}" exceeds the 4MB limit`);
                      continue;
                    }
                    validFiles.push(file);
                  }
                  if (!validFiles.length) return;

                  setCommentFiles((prev) => {
                    const existingKeys = new Set(
                      prev.map((f) => `${f.name}_${f.size}`)
                    );
                    const merged = [...prev];
                    for (const file of validFiles) {
                      const key = `${file.name}_${file.size}`;
                      if (!existingKeys.has(key)) {
                        merged.push(file);
                        existingKeys.add(key);
                      }
                    }
                    return merged;
                  });
                }}
              />
              <div style={{ fontSize: "11.5px", color: "#666", marginTop: "4px" }}>
                Supported: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX — max 4MB each
              </div>
              {commentFiles.length > 0 && (
                <ul style={{ marginTop: "8px", paddingLeft: "18px" }}>
                  {commentFiles.map((file, index) => (
                    <li key={`${file.name}-${index}`}>
                      {file.name}{" "}
                      <span
                        onClick={() =>
                          setCommentFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                        style={{ color: "red", cursor: "pointer" }}
                      >
                        (remove)
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
              <Button variant="success" onClick={handleSendComment} disabled={submitting}>
                {submitting ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrievanceDetails;
